import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PauseIcon, PlayIcon, ZoomInIcon, XIcon } from "lucide-react";
import { Section, Reveal, Eyebrow, SectionTitle } from "./ui";
import { previews, previewChips } from "./data";
export function PreviewGallery() {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [playing, setPlaying] = useState(true);
  useEffect(() => {
    if (!playing || zoom) return;
    const timer = window.setInterval(() => {
      setActive(current => (current + 1) % previews.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [playing, zoom]);
  return <Section id="previsualizacion" className="bg-[#f5f9fd]" data-id="element-152" data-mp="nlc2xy3uxmbb">
      <Reveal className="mx-auto max-w-2xl text-center" data-id="element-153" data-mp="njr96apda7ei">
        <Eyebrow data-id="element-154" data-mp="2udcs8pzjtcsm">Mirá el material antes de comprar</Eyebrow>
        <SectionTitle data-id="element-155" data-mp="2pd8e36m44u5k">No tenés que imaginar qué vas a recibir. Miralo por dentro.</SectionTitle>
        <p className="mt-4 text-sm leading-relaxed text-[var(--muted)] sm:text-base" data-id="element-156" data-mp="3tvcw4trlct5z">
          Más de 500 páginas con pictogramas, checklists, tablas y listas de estrategias: material
          de consulta rápida, no párrafos interminables.
        </p>
      </Reveal>

      <Reveal delay={0.08} data-id="element-157" data-mp="7mtro4y682p6">
        <div className="mx-auto mt-7 w-full max-w-3xl sm:mt-10" data-id="element-158" data-mp="ceuzneob379e">
          <div className="relative overflow-hidden rounded-2xl border-4 border-white bg-white p-1.5 shadow-[0_26px_60px_-30px_rgba(23,50,77,0.55)] sm:rounded-[1.75rem] sm:p-3" data-id="element-159" data-mp="cg6137ua42xc">
            <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-[#edf4fc] sm:aspect-[16/10] sm:rounded-2xl" data-id="element-160" data-mp="le8aaao621vp">
              <AnimatePresence mode="wait" data-id="element-161" data-mp="qfe7akov0bb1">
                <motion.img key={previews[active].src} src={previews[active].src} alt={previews[active].label} initial={{
                opacity: 0,
                scale: 1.02
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.28,
                ease: [0.23, 1, 0.32, 1]
              }} className="h-full w-full cursor-zoom-in object-contain" onClick={() => setZoom(true)} loading="lazy" data-id="element-162" data-mp="ve8vk25lis2j" />
              </AnimatePresence>
              <button type="button" onClick={() => setZoom(true)} className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[var(--ink)] shadow-md" aria-label="Ampliar muestra" data-id="element-163" data-mp="2u3r11p4rw6pg">
                <ZoomInIcon className="h-4 w-4" aria-hidden data-id="element-164" data-mp="3htl8tsa8j52u" />
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-3" data-id="element-165" data-mp="3cv0ajyejmvh1">
            <button type="button" onClick={() => setPlaying(v => !v)} className="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-[#d8e6f4] bg-white text-[var(--accent)] shadow-sm" aria-label={playing ? "Pausar carrusel" : "Reanudar carrusel"} data-id="element-166" data-mp="3qtgnw86wvk85">
              {playing ? <PauseIcon className="h-3.5 w-3.5" aria-hidden data-id="element-167" data-mp="e7rsfirvqjd7" /> : <PlayIcon className="h-3.5 w-3.5" aria-hidden data-id="element-168" data-mp="ch1uhko4ken" />}
            </button>
            <p className="max-w-xs text-center text-xs font-bold leading-snug text-[var(--ink)] sm:max-w-md sm:text-sm" data-id="element-169" data-mp="5bk7o48h2uno">
              {previews[active].label}
            </p>
          </div>

          <div className="mt-4 flex justify-center gap-1.5" aria-label="Progreso del carrusel" data-id="element-170" data-mp="3rhyz9lm0nbs2">
            {previews.map((p, index) => <button key={p.src} type="button" onClick={() => setActive(index)} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${index === active ? "w-6 bg-[var(--accent)]" : "w-1.5 bg-[#cdd9e4]"}`} aria-label={`Ver muestra ${index + 1}`} data-id="element-171" data-mp="3hql1pjdmkm1k" data-mpi={index} />)}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.12} data-id="element-172" data-mp="aypin0jxlf7m">
        <ul className="mt-6 flex flex-wrap justify-center gap-2" data-id="element-173" data-mp="65mmcknrrrnq">
          {previewChips.map(c => <li key={c} className="rounded-full bg-white px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--accent)] shadow-sm" data-id="element-174" data-mp="17bhkocc07cd">
              {c}
            </li>)}
        </ul>
      </Reveal>

      <AnimatePresence data-id="element-175" data-mp="3ngcmklzg5y6w">
        {zoom && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => setZoom(false)} className="fixed inset-0 z-[70] flex items-center justify-center bg-[var(--ink)]/90 p-4 backdrop-blur-sm" data-id="element-176" data-mp="3jdg18niivnz9">
            <button type="button" onClick={() => setZoom(false)} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[var(--ink)] shadow-lg" aria-label="Cerrar ampliación" data-id="element-177" data-mp="1k5r57cu8sydf">
              <XIcon className="h-5 w-5" aria-hidden data-id="element-178" data-mp="1yu470qg0hbfu" />
            </button>
            <motion.img key={previews[active].src} src={previews[active].src} alt={previews[active].label} initial={{
          scale: 0.96,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-h-[88vh] max-w-full rounded-xl bg-white object-contain shadow-2xl" data-id="element-179" data-mp="1ywgol2huxjsd" />
          </motion.div>}
      </AnimatePresence>
    </Section>;
}